import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ar, type Dictionary } from '@/shared/config/locale/ar';
import { en } from '@/shared/config/locale/en';

export type LocaleCode = 'ar' | 'en';

const DICTIONARIES: Record<LocaleCode, Dictionary> = { ar, en };
const STORAGE_KEY = 'aoun.locale';

type LocaleContextValue = {
  locale: LocaleCode;
  dir: 'rtl' | 'ltr';
  t: Dictionary;
  setLocale: (locale: LocaleCode) => void;
  toggleLocale: () => void;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

function readStoredLocale(): LocaleCode {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'en' || stored === 'ar' ? stored : 'ar';
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleCode>(readStoredLocale);

  // ⚠️ `dir` is written to <html>, not to a wrapper div.
  //
  // §23.10 makes the panel bilingual, so the whole document flips. Setting direction on
  // an inner element leaves scrollbars, native form controls, and browser dialogs on the
  // wrong side — the parts of a page an application cannot restyle. This is also why
  // §5.6 forbids physical Tailwind utilities: `ml-4` survives the flip and looks
  // correct in Arabic, then breaks visibly in English.
  useEffect(() => {
    const dictionary = DICTIONARIES[locale];
    document.documentElement.lang = dictionary.meta.locale;
    document.documentElement.dir = dictionary.meta.dir;
    localStorage.setItem(STORAGE_KEY, locale);
  }, [locale]);

  const setLocale = useCallback((next: LocaleCode) => setLocaleState(next), []);
  const toggleLocale = useCallback(
    () => setLocaleState((current) => (current === 'ar' ? 'en' : 'ar')),
    [],
  );

  const value = useMemo<LocaleContextValue>(() => {
    const dictionary = DICTIONARIES[locale];
    return {
      locale,
      dir: dictionary.meta.dir as 'rtl' | 'ltr',
      t: dictionary,
      setLocale,
      toggleLocale,
    };
  }, [locale, setLocale, toggleLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);

  if (!context) {
    // Thrown rather than falling back to Arabic: a silent fallback would render a
    // component outside the provider in one language while the rest of the page is in
    // another, which is far harder to notice than a crash in development.
    throw new Error('useLocale must be used inside <LocaleProvider>.');
  }

  return context;
}
